import type { InputType, OptionList, RuleType } from 'react-querybuilder';

import type { SegmentOptions } from './types';
import type { SegmentOptionsData } from '../data/response';
import type {
  SegmentAttributeGroup,
  SegmentConditionGroupOperator,
  SegmentOperator,
  SegmentPredefinedValue,
} from '../data/types';

export const validator = (r: RuleType) => !!r.value;

// Transform predefined values to QueryBuilder values format
export function transformValues(values: SegmentPredefinedValue[]): OptionList | undefined {
  if (!values || values.length === 0) return undefined;

  return values.map((value) => ({
    name: value.id,
    value: value.id,
    label: value.name,
  }));
}
// Transform condition group operators to QueryBuilder combinators format
export function transformCombinators(conditionGroupOperators: SegmentConditionGroupOperator[]) {
  return conditionGroupOperators.map((operator) => ({
    name: operator.id,
    value: operator.id,
    label: operator.name,
  }));
}

// Transform operators to QueryBuilder operators format
export function transformOperators(operators: SegmentOperator[]) {
  return operators.map((operator) => ({
    name: operator.id,
    value: operator.id,
    label: operator.name,
  }));
}

// Transform attribute groups to QueryBuilder fields format (OptionList)
export function transformFields(
  attributeGroups: SegmentAttributeGroup[],
  operators: SegmentOperator[],
  values: SegmentPredefinedValue[]
): OptionList {
  const result = attributeGroups.flatMap((group) =>
    group.sub_groups.flatMap((subGroup) => ({
      label: `${group.name} -> ${subGroup.name}`,
      options: subGroup.attributes.map((attribute) => {
        const fieldOperators = getOperators(attribute.operators, operators);
        const fieldValues = getValues(
          fieldOperators.flatMap((operator) => operator.values),
          values
        );
        return {
          name: attribute.id,
          label: attribute.name,
          id: attribute.id,
          operators: transformOperators(fieldOperators),
          inputType: mapInputType(fieldOperators[0].value_type),
          valueEditorType: mapValueType(fieldOperators[0].value_type),
          values: transformValues(fieldValues),
          validator,
        };
      }),
    }))
  );

  return result;
}

// Main transformer function that processes the entire API response
export function transformSegmentOptions(data: SegmentOptionsData): SegmentOptions {
  return {
    combinators: transformCombinators(data.condition_group_operators),
    operators: transformOperators(data.operators),
    fields: transformFields(data.attribute_groups, data.operators, data.predefined_values),
    predefinedValues: transformValues(data.predefined_values),
  };
}

// Segment Options Util functions
function getOperators(attributeOperators: string[], operators: SegmentOperator[]) {
  return operators.filter((operator) => attributeOperators.includes(operator.id));
}

function getValues(attributeValues: string[], values: SegmentPredefinedValue[]) {
  return values.filter((value) => attributeValues.includes(value.id));
}

function mapValueType(valueType: SegmentOperator['value_type']) {
  switch (valueType) {
    case 'predefined':
      return 'select';
    default:
      return 'text';
  }
}

function mapInputType(valueType: SegmentOperator['value_type']): InputType {
  switch (valueType) {
    case 'number':
      return 'number';
    default:
      return null;
  }
}

// Various util functions
export function indexById<R extends { id: string }>(list: R[]): Record<string, R> {
  return list.reduce((acc, item) => {
    const { id, ...rest } = item;
    acc[id] = rest;
    return acc;
  }, {});
}
